import { createSlice } from '@reduxjs/toolkit';
import { BASLIK_DEFAULTS } from '../config/baslikConfig/defaults';

// Başlık verisi PAFTA BAZINDA tutulur: her paftanın (Kuvvetli Akım, Şantiye…)
// kendi başlık alanları/kilitleri/biçimleri vardır. `activeTab` o an düzenlenen
// paftayı gösterir; reducer'lar aktif paftanın kaydını değiştirir. Böylece action
// payload'ları ve selectBaslikDefaults imzası DEĞİŞMEDEN kalır — tüm mevcut
// tüketiciler (BaslikPanel, baslikSections, PanelSettingsPopup, binding çözücü,
// serializer) dokunulmadan çalışır. Aktif pafta DrawingApp'te projectTabs
// activeTabId ile senkronlanır (setBaslikActiveTab).

const FALLBACK_TAB = '_default';

// Her pafta KENDİ derin kopyasını almalı. Shallow clone ({...BASLIK_DEFAULTS})
// nested section nesnelerini sabitle paylaşır → bir paftayı düzenlemek diğerini
// (ve sabiti) bozardı. BASLIK_DEFAULTS saf veri olduğu için JSON klon güvenli.
function freshDefaults() {
  return JSON.parse(JSON.stringify(BASLIK_DEFAULTS));
}

const initialState = {
  byTab: { [FALLBACK_TAB]: freshDefaults() },
  activeTab: FALLBACK_TAB,
  selectedCell: null, // UI-global: biçim barı için hangi hücre seçili ("section.field")
};

// Aktif paftanın defaults kaydı (yoksa oluştur). Immer draft üzerinde mutasyon güvenli.
function activeDefaults(state) {
  const id = state.activeTab || FALLBACK_TAB;
  if (!state.byTab[id]) state.byTab[id] = freshDefaults();
  return state.byTab[id];
}

const baslikSlice = createSlice({
  name: 'baslik',
  initialState,
  reducers: {
    // Aktif paftayı değiştir (DrawingApp projectTabs.activeTabId'e bağlar).
    setBaslikActiveTab: (state, action) => {
      const id = action.payload || FALLBACK_TAB;
      if (!state.byTab[id]) state.byTab[id] = freshDefaults();
      state.activeTab = id;
    },

    updateBaslikDefaults: (state, action) => {
      const { section, changes } = action.payload;
      const d = activeDefaults(state);
      if (section) {
        if (d[section]) Object.assign(d[section], changes);
      } else {
        Object.assign(d, changes);
      }
    },
    resetBaslikDefaults: (state) => {
      state.byTab[state.activeTab || FALLBACK_TAB] = freshDefaults();
      state.selectedCell = null;
    },

    setSelectedCell: (state, action) => {
      // payload: "sectionKey.fieldKey" veya null
      state.selectedCell = action.payload;
    },

    toggleCellLock: (state, action) => {
      // payload: "sectionKey.fieldKey"
      const cellId = action.payload;
      const d = activeDefaults(state);
      if (d.cellLocks) {
        d.cellLocks[cellId] = !d.cellLocks[cellId];
      }
    },

    updateCellFormat: (state, action) => {
      // payload: { cellId: "sectionKey.fieldKey", format: { align?, fontSize? } }
      const { cellId, format } = action.payload;
      const d = activeDefaults(state);
      if (!d.cellFormats) d.cellFormats = {};
      d.cellFormats[cellId] = {
        ...(d.cellFormats[cellId] || {}),
        ...format,
      };
    },
  },
});

export const {
  updateBaslikDefaults,
  resetBaslikDefaults,
  setSelectedCell,
  toggleCellLock,
  updateCellFormat,
  setBaslikActiveTab,
} = baslikSlice.actions;

// ─── Selectors ─────────────────────────────────────────
// Aktif paftanın defaults kaydı. Eski .jbz (byTab yok, tek `defaults` alanı olan
// şekil) yüklenince o `defaults`'a düşer → başlık boş görünmez; kullanıcı ilk
// düzenlemede updateBaslikDefaults byTab[activeTab]'e yazar ve yeni şekle göçer.
const EMPTY_DEFAULTS = {};
export function getActiveBaslikDefaults(state) {
  const b = state?.baslik;
  if (!b) return null;
  const tabbed = b.byTab && b.byTab[b.activeTab];
  if (tabbed) return tabbed;
  return b.defaults || null; // geriye dönük (eski .jbz)
}
export const selectBaslikDefaults = (state) =>
  getActiveBaslikDefaults(state) || EMPTY_DEFAULTS;

export const selectProjeSorumlusu = (state) => selectBaslikDefaults(state).projeSorumlusu;
export const selectProjeUygulamaSorumlusu = (state) => selectBaslikDefaults(state).projeUygulamaSorumlusu;
export const selectProjeMuellifi = (state) => selectBaslikDefaults(state).projeMuellifi;
export const selectProjeyiYaptiran = (state) => selectBaslikDefaults(state).projeyiYaptiran;
export const selectArsaOzellikleri = (state) => selectBaslikDefaults(state).arsaOzellikleri;
export const selectYapiOzellikleri = (state) => selectBaslikDefaults(state).yapiOzellikleri;

// Yeni selectors
// SABİT boş fallback'ler — `|| {}` her çağrıda yeni obje döndürüp useSelector'ı
// unstable yapardı (sonsuz re-render riski). Daima aynı referans dönmeli.
const EMPTY_OBJ = Object.freeze({});
export const selectSelectedCell = (state) => state.baslik.selectedCell;
export const selectCellLocks = (state) => selectBaslikDefaults(state).cellLocks || EMPTY_OBJ;
export const selectCellFormats = (state) => selectBaslikDefaults(state).cellFormats || EMPTY_OBJ;

export default baslikSlice.reducer;
